<template>
  <div class="announcement-items-editor">
    <div class="row">
      <div class="col-lg-7">
        <div v-for="(item, idx) in items" :key="'item-' + idx" class="editor-item-card mb-3 p-3 border rounded bg-white">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <strong class="small text-muted">Item {{ idx + 1 }}</strong>
            <div>
              <button type="button" class="btn btn-sm btn-outline-secondary mr-1" :disabled="idx === 0" @click="moveUp(idx)" title="Move up">↑</button>
              <button type="button" class="btn btn-sm btn-outline-secondary mr-1" :disabled="idx === items.length - 1" @click="moveDown(idx)" title="Move down">↓</button>
              <button type="button" class="btn btn-sm btn-outline-danger" @click="removeItem(idx)" title="Remove">✕ Remove</button>
            </div>
          </div>

          <div class="form-group mb-2">
            <label class="small font-weight-bold mb-1">Title</label>
            <input type="text" class="form-control form-control-sm" v-model="item.title" placeholder="e.g. About substitute tutors">
          </div>

          <div class="form-group mb-2">
            <label class="small font-weight-bold mb-1">Body (blank line = new paragraph)</label>
            <div class="body-toolbar mb-1">
              <button type="button" class="btn btn-sm btn-outline-secondary" title="Bold" @click="applyBold(idx)"><strong>B</strong></button>
              <button type="button" class="btn btn-sm btn-outline-secondary" title="Italic" @click="applyItalic(idx)"><em>I</em></button>
              <button type="button" class="btn btn-sm btn-outline-secondary" title="Bulleted list" @click="applyList(idx)">&bull; List</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" title="Insert link" @click="applyLink(idx)">🔗 Link</button>
            </div>
            <textarea
              :ref="'bodyInput' + idx"
              class="form-control form-control-sm"
              rows="3"
              v-model="item.body"
              placeholder="Enter the content here. Select text and use the buttons above for bold/italic/list/link."
            ></textarea>
            <div class="small text-muted mt-1">
              Tip: select text first, then click Bold/Italic/List/Link. Lists use one line per item.
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6 mb-2">
              <label class="small font-weight-bold mb-1">Badge (solid red box, leave blank to hide)</label>
              <input type="text" class="form-control form-control-sm" v-model="item.badge" placeholder="e.g. Campaign">
            </div>
            <div class="form-group col-md-6 mb-2 d-flex align-items-end">
              <div class="form-check form-check-inline mb-1">
                <input class="form-check-input" type="checkbox" v-model="item.is_open" :id="'open-' + idx">
                <label class="form-check-label small" :for="'open-' + idx">Open by default</label>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6 mb-2">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="item.is_featured" :id="'featured-' + idx">
                <label class="form-check-label small" :for="'featured-' + idx">Highlight (yellow box)</label>
              </div>
            </div>
            <div class="form-group col-md-6 mb-2">
              <label class="small font-weight-bold mb-1">Warning tag (red outline, leave blank to hide)</label>
              <input type="text" class="form-control form-control-sm" v-model="item.caution_label" placeholder="e.g. Please Check">
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6 mb-0">
              <label class="small font-weight-bold mb-1">Button label (optional)</label>
              <input type="text" class="form-control form-control-sm" v-model="item.cta_label" placeholder="e.g. View materials">
            </div>
            <div class="form-group col-md-6 mb-0">
              <label class="small font-weight-bold mb-1">Button URL (optional)</label>
              <input type="text" class="form-control form-control-sm" v-model="item.cta_url" placeholder="https://...">
              <div class="small text-muted mt-1">
                Both label and URL are required for the button to appear. "https://" is added automatically if you leave it off.
              </div>
            </div>
          </div>

          <div class="form-row mt-2">
            <div class="form-group col-12 mb-0">
              <label class="small font-weight-bold mb-1 d-block">Visible to</label>
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="checkbox" v-model="item.show_on_mypage" :id="'mypage-' + idx">
                <label class="form-check-label small" :for="'mypage-' + idx">MyPage members</label>
              </div>
              <div class="form-check form-check-inline">
                <input class="form-check-input" type="checkbox" v-model="item.show_on_netenglish" :id="'netenglish-' + idx">
                <label class="form-check-label small" :for="'netenglish-' + idx">NetEnglish members</label>
              </div>
              <div class="small text-muted mt-1">
                Uncheck a site to hide this item there. Both are on by default.
              </div>
            </div>
          </div>
        </div>

        <button type="button" class="btn btn-blue-gradient text-white btn-sm" @click="addItem">+ Add item</button>

        <div v-if="items.length === 0" class="text-muted small mt-2">
          No items yet. Click "+ Add item" to create one.
        </div>

        <hr>

        <h6 class="font-weight-bold small">Tutor Cancellation Notice (optional)</h6>
        <p class="small text-muted">
          Shown as a separate dashed box below the announcement items. Leave both fields blank to hide it.
        </p>
        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="small font-weight-bold mb-1">Title</label>
            <input type="text" class="form-control form-control-sm" name="leave_title" v-model="leaveTitle" placeholder="〈 講師休講のご案内 〉">
          </div>
          <div class="form-group col-md-8 mb-0">
            <label class="small font-weight-bold mb-1">Text (one line per tutor entry)</label>
            <textarea class="form-control form-control-sm" name="leave_text" rows="4" v-model="leaveText" placeholder="Iris講師：6月12日〜6月13日&#10;Kim先生：6月11日（木）17:30〜23:55 体調不良 急で申し訳ありません"></textarea>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group col-12 mb-0">
            <label class="small font-weight-bold mb-1 d-block">Visible to</label>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="checkbox" id="leave-mypage" v-model="leaveShowOnMypage">
              <label class="form-check-label small" for="leave-mypage">MyPage members</label>
            </div>
            <div class="form-check form-check-inline">
              <input class="form-check-input" type="checkbox" id="leave-netenglish" v-model="leaveShowOnNetenglish">
              <label class="form-check-label small" for="leave-netenglish">NetEnglish members</label>
            </div>
            <div class="small text-muted mt-1">
              Both are on by default so tutor cancellations appear on MyPage and NetEnglish.
            </div>
            <input type="hidden" name="leave_show_on_mypage" :value="leaveShowOnMypage ? 1 : 0">
            <input type="hidden" name="leave_show_on_netenglish" :value="leaveShowOnNetenglish ? 1 : 0">
          </div>
        </div>
      </div>

      <div class="col-lg-5">
        <div class="preview-sticky">
          <div class="small text-muted mb-2">Preview (click a row to check open/close)</div>

          <div class="notice-accordion-wrap">
            <div class="notice-head">お知らせ</div>
            <div class="notice-box">
              <div
                v-for="(item, idx) in items"
                :key="'preview-' + idx"
                class="item"
                :class="{ open: item.is_open, featured: item.is_featured, caution: !!item.caution_label }"
              >
                <div class="item-head" @click="item.is_open = !item.is_open">
                  <span v-if="item.badge" class="badge">{{ item.badge }}</span>
                  <span class="item-title">{{ item.title || '(No title entered)' }}</span>
                  <span v-if="item.caution_label" class="tag-caution">{{ item.caution_label }}</span>
                  <span class="audience-tags">
                    <span v-if="item.show_on_mypage" class="tag-audience">MyPage</span>
                    <span v-if="item.show_on_netenglish" class="tag-audience">NetEnglish</span>
                    <span v-if="!item.show_on_mypage && !item.show_on_netenglish" class="tag-audience tag-audience-off">Hidden</span>
                  </span>
                  <span class="toggle"></span>
                </div>
                <div class="item-body" :class="{ open: item.is_open }">
                  <div class="item-body-inner">
                    <div v-html="renderBodyHtml(item.body)"></div>
                    <a v-if="item.cta_label && item.cta_url" class="cta" href="javascript:void(0)" @click.stop>▶ {{ item.cta_label }}</a>
                  </div>
                </div>
              </div>

              <div v-if="items.length === 0" class="text-muted small p-3">
                No items to preview yet.
              </div>
            </div>

            <div v-if="leaveLines.length > 0" class="leave">
              <div class="leave-head">{{ displayLeaveTitle }}</div>
              <div class="leave-audience mb-2">
                <span v-if="leaveShowOnMypage" class="tag-audience">MyPage</span>
                <span v-if="leaveShowOnNetenglish" class="tag-audience">NetEnglish</span>
                <span v-if="!leaveShowOnMypage && !leaveShowOnNetenglish" class="tag-audience tag-audience-off">Hidden</span>
              </div>
              <div v-for="(line, lineIdx) in leaveLines" :key="'leave-' + lineIdx" class="leave-line">
                {{ line }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <input type="hidden" :name="hiddenFieldName" :value="itemsJson">
  </div>
</template>

<script>
const DEFAULT_ITEM = () => ({
  title: '',
  body: '',
  badge: '',
  is_open: false,
  is_featured: false,
  caution_label: '',
  cta_label: '',
  cta_url: '',
  show_on_mypage: true,
  show_on_netenglish: true,
});

// Older saved items stored a plain `is_caution` boolean with a fixed
// "要確認" label. Normalize those into the new free-text field on load.
// Missing audience flags default to both platforms (backward compatible).
function normalizeItem(item) {
  const normalized = Object.assign(DEFAULT_ITEM(), item);
  if (!normalized.caution_label && item && item.is_caution) {
    normalized.caution_label = '要確認';
  }
  delete normalized.is_caution;
  if (item && !Object.prototype.hasOwnProperty.call(item, 'show_on_mypage')) {
    normalized.show_on_mypage = true;
  }
  if (item && !Object.prototype.hasOwnProperty.call(item, 'show_on_netenglish')) {
    normalized.show_on_netenglish = true;
  }
  return normalized;
}

export default {
  name: 'AnnouncementItemsEditorComponent',
  props: {
    initialItems: {
      type: Array,
      default: () => [],
    },
    initialLeaveTitle: {
      type: String,
      default: '',
    },
    initialLeaveText: {
      type: String,
      default: '',
    },
    initialLeaveShowOnMypage: {
      type: Boolean,
      default: true,
    },
    initialLeaveShowOnNetenglish: {
      type: Boolean,
      default: true,
    },
    hiddenFieldName: {
      type: String,
      default: 'items_json',
    },
  },
  data() {
    return {
      items: [],
      leaveTitle: '',
      leaveText: '',
      leaveShowOnMypage: true,
      leaveShowOnNetenglish: true,
    };
  },
  computed: {
    itemsJson() {
      return JSON.stringify(this.items);
    },
    leaveLines() {
      return (this.leaveText || '')
        .split(/\r\n|\n|\r/)
        .map((line) => line.trim())
        .filter((line) => line !== '');
    },
    displayLeaveTitle() {
      return this.leaveTitle.trim() || '〈 講師休講のご案内 〉';
    },
  },
  methods: {
    addItem() {
      this.items.push(DEFAULT_ITEM());
    },
    removeItem(idx) {
      if (window.confirm('Delete this item?')) {
        this.items.splice(idx, 1);
      }
    },
    moveUp(idx) {
      if (idx === 0) {
        return;
      }
      const previous = this.items[idx - 1];
      this.items.splice(idx - 1, 1, this.items[idx]);
      this.items.splice(idx, 1, previous);
    },
    moveDown(idx) {
      if (idx === this.items.length - 1) {
        return;
      }
      const next = this.items[idx + 1];
      this.items.splice(idx + 1, 1, this.items[idx]);
      this.items.splice(idx, 1, next);
    },
    getBodyTextarea(idx) {
      const ref = this.$refs['bodyInput' + idx];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    // Wraps the current text selection with the given markers (e.g. ** **
    // for bold, _ _ for italic). Falls back to inserting a placeholder word
    // when nothing is selected so staff can see where to type.
    wrapSelection(idx, before, after) {
      const el = this.getBodyTextarea(idx);
      if (!el) {
        return;
      }
      const value = this.items[idx].body || '';
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = value.slice(start, end) || 'text';
      const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
      this.items[idx].body = newValue;
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + before.length;
        el.selectionEnd = start + before.length + selected.length;
      });
    },
    applyBold(idx) {
      this.wrapSelection(idx, '**', '**');
    },
    applyItalic(idx) {
      this.wrapSelection(idx, '_', '_');
    },
    // Prefixes every line touched by the current selection with "- ",
    // turning the selected lines into a bulleted list.
    applyList(idx) {
      const el = this.getBodyTextarea(idx);
      if (!el) {
        return;
      }
      const value = this.items[idx].body || '';
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      let lineEnd = value.indexOf('\n', end);
      if (lineEnd === -1) {
        lineEnd = value.length;
      }
      const selectedLines = value.slice(lineStart, lineEnd) || 'List item';
      const prefixed = selectedLines
        .split('\n')
        .map((line) => (/^\s*-\s+/.test(line) ? line : '- ' + line))
        .join('\n');
      const newValue = value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
      this.items[idx].body = newValue;
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = lineStart;
        el.selectionEnd = lineStart + prefixed.length;
      });
    },
    // Prompts for a URL and wraps the current selection (or a placeholder)
    // in [label](url) markdown-style markers, matching parseInline() below
    // and AnnouncementItemsBuilder::parseInline() on the PHP side.
    applyLink(idx) {
      const url = window.prompt('Link URL (e.g. https://example.com):', 'https://');
      if (!url || !url.trim()) {
        return;
      }
      const el = this.getBodyTextarea(idx);
      if (!el) {
        return;
      }
      const value = this.items[idx].body || '';
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const label = value.slice(start, end) || 'リンクテキスト';
      const before = '[';
      const after = '](' + url.trim() + ')';
      const newValue = value.slice(0, start) + before + label + after + value.slice(end);
      this.items[idx].body = newValue;
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + before.length;
        el.selectionEnd = start + before.length + label.length;
      });
    },
    // Mirrors AnnouncementItemsBuilder::textToHtml() on the PHP side so the
    // live preview matches what will actually be saved.
    renderBodyHtml(text) {
      if (!text) {
        return '';
      }
      const blocks = text.split(/\r\n\r\n|\n\n|\r\r/);
      let html = '';
      blocks.forEach((rawBlock) => {
        const block = rawBlock.trim();
        if (!block) {
          return;
        }
        html += this.renderLines(block.split(/\r\n|\n|\r/));
      });
      return html;
    },
    // Switches between <p> and <ul> whenever a "- " list line starts/stops,
    // so intro text and bullet lists can be mixed within the same block.
    renderLines(lines) {
      let html = '';
      let paragraphLines = [];
      let listLines = [];

      lines.forEach((line) => {
        if (line.trim() === '') {
          return;
        }
        if (/^\s*-\s+/.test(line)) {
          if (paragraphLines.length > 0) {
            html += this.renderParagraph(paragraphLines);
            paragraphLines = [];
          }
          listLines.push(line);
        } else {
          if (listLines.length > 0) {
            html += this.renderList(listLines);
            listLines = [];
          }
          paragraphLines.push(line);
        }
      });

      if (paragraphLines.length > 0) {
        html += this.renderParagraph(paragraphLines);
      }
      if (listLines.length > 0) {
        html += this.renderList(listLines);
      }

      return html;
    },
    renderParagraph(lines) {
      return '<p>' + this.parseInline(lines.join('\n')).replace(/\n/g, '<br>') + '</p>';
    },
    renderList(lines) {
      const items = lines
        .map((line) => '<li>' + this.parseInline(line.replace(/^\s*-\s+/, '')) + '</li>')
        .join('');
      return '<ul>' + items + '</ul>';
    },
    parseInline(text) {
      const escaped = this.escapeHtml(text);
      return escaped
        .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, url) => {
          const safeUrl = this.normalizeBodyLinkUrl(url);
          if (!safeUrl) {
            return label;
          }
          return '<a class="faq" href="' + safeUrl + '" target="_blank" rel="noopener">' + label + '</a>';
        })
        .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
        .replace(/_(.+?)_/gs, '<em>$1</em>');
    },
    // Mirrors AnnouncementItemsBuilder::normalizeCtaUrl() so preview links
    // match saved output: auto-prepend https:// for bare domains/paths,
    // reject unsafe schemes like javascript:.
    normalizeBodyLinkUrl(url) {
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
        return '';
      }
      return 'https://' + url.replace(/^\/+/, '');
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
  },
  mounted() {
    this.items = Array.isArray(this.initialItems) && this.initialItems.length > 0
      ? this.initialItems.map(normalizeItem)
      : [];
    this.leaveTitle = this.initialLeaveTitle || '';
    this.leaveText = this.initialLeaveText || '';
    this.leaveShowOnMypage = this.initialLeaveShowOnMypage !== false;
    this.leaveShowOnNetenglish = this.initialLeaveShowOnNetenglish !== false;
  },
};
</script>

<style scoped>
.preview-sticky {
  position: sticky;
  top: 12px;
}

.editor-item-card {
  background: #fff;
}

.body-toolbar {
  display: flex;
  gap: 4px;
}

.body-toolbar .btn {
  padding: 2px 10px;
  line-height: 1.4;
}

.notice-accordion-wrap {
  --mt-blue: #29abe2;
  --mt-blue-dark: #1f8fc0;
  --mt-red: #e2231a;
  --mt-border: #6fc7ef;
  --mt-ink: #333;
  --mt-sub: #666;
  --mt-new: #fff7e0;
  --mt-new-border: #f4b400;
  font-size: 14px;
}

.notice-head {
  display: inline-block;
  background: var(--mt-blue);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  padding: 6px 16px;
  border-radius: 16px 16px 16px 0;
  margin-bottom: 10px;
}

.notice-box {
  border: 2px dashed var(--mt-border);
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.notice-accordion-wrap .item {
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  background: #fff;
}

.notice-accordion-wrap .item:last-child {
  margin-bottom: 0;
}

.notice-accordion-wrap .item-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fafcfe;
  padding: 10px 12px;
  color: var(--mt-red);
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}

.notice-accordion-wrap .item-title {
  flex: 1;
}

.notice-accordion-wrap .toggle {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--mt-blue);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 15px;
  font-weight: 700;
  transition: transform 0.2s ease;
}

.notice-accordion-wrap .toggle::before {
  content: "+";
}

.notice-accordion-wrap .item.open > .item-head .toggle {
  transform: rotate(180deg);
}

.notice-accordion-wrap .item.open > .item-head .toggle::before {
  content: "\2212";
}

.notice-accordion-wrap .badge {
  flex: 0 0 auto;
  background: var(--mt-red);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 4px;
}

.notice-accordion-wrap .tag-caution {
  flex: 0 0 auto;
  background: #fff0ef;
  color: var(--mt-red);
  border: 1px solid var(--mt-red);
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
}

.audience-tags,
.leave-audience {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.tag-audience {
  flex: 0 0 auto;
  background: #eef7fc;
  color: var(--mt-blue-dark);
  border: 1px solid var(--mt-border);
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
}

.tag-audience-off {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
}

.notice-accordion-wrap .item-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease;
}

.notice-accordion-wrap .item.open > .item-body {
  max-height: 1000px;
}

.notice-accordion-wrap .item-body-inner {
  padding: 2px 12px 12px;
  color: var(--mt-ink);
  font-size: 13px;
}

.notice-accordion-wrap .item-body-inner p {
  margin: 0 0 8px;
}

.notice-accordion-wrap .item-body-inner ul {
  margin: 0 0 8px;
  padding-left: 18px;
}

.notice-accordion-wrap .item-body-inner li {
  margin: 0 0 4px;
}

.notice-accordion-wrap .item.featured {
  border: 1px solid var(--mt-new-border);
  background: var(--mt-new);
}

.notice-accordion-wrap .item.featured .item-head {
  background: transparent;
}

.notice-accordion-wrap .item.caution {
  border-left: 4px solid var(--mt-red);
}

.notice-accordion-wrap .cta {
  display: inline-block;
  margin-top: 4px;
  background: var(--mt-blue);
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 6px;
}

.notice-accordion-wrap .item-body-inner .faq {
  color: var(--mt-blue);
  font-weight: 700;
  text-decoration: none;
}

.notice-accordion-wrap .item-body-inner .faq:hover {
  text-decoration: underline;
}

.notice-accordion-wrap .leave {
  margin-top: 14px;
  border: 2px dashed var(--mt-border);
  border-radius: 10px;
  background: #fff;
  padding: 14px;
}

.notice-accordion-wrap .leave-head {
  text-align: center;
  color: var(--mt-red);
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 12px;
  letter-spacing: 0.04em;
}

.notice-accordion-wrap .leave-line {
  padding: 8px 2px;
  border-bottom: 1px dashed #e3e8ee;
  font-size: 14px;
}

.notice-accordion-wrap .leave-line:last-child {
  border-bottom: 0;
}
</style>
